import { ImageCoordinates } from 'lib/geometry';
import { FloorplanV2Plan } from 'lib/api';
import FloorplanCollection from 'lib/floorplan-collection';

// A measurement represents a line on a floorplan used to scale it properly.
//
// Measurements have two 2d points with units of pixels on an image, plus metadata about the
// distance between those points in meters. This information can be used to compute a pixels per
// meter scale for the image.
type Measurement = {
  pointA: ImageCoordinates;
  pointB: ImageCoordinates;
  userEnteredLength: {
    feetText: string;
    inchesText: string;
  };
  // meters
  computedLength: number;
  // pixels-per-meter
  computedScale: number;
};

const Measurement = {
  fromFloorplanAPIResponse: (plan: FloorplanV2Plan): Measurement => ({
    pointA: ImageCoordinates.create(
      plan.measurement_point_a_x_pixels,
      plan.measurement_point_a_y_pixels
    ),
    pointB: ImageCoordinates.create(
      plan.measurement_point_b_x_pixels,
      plan.measurement_point_b_y_pixels
    ),
    userEnteredLength: {
      feetText: plan.measurement_user_entered_length_feet,
      inchesText: plan.measurement_user_entered_length_inches,
    },
    computedLength: plan.measurement_computed_length_meters, // meters
    computedScale: plan.image_pixels_per_meter, // pixels-per-meter
  }),
  toFloorplanScaleUpdate: (
    origin: { x: number; y: number },
    measurement: Measurement,
    resetOrigin: boolean
  ): Pick<
    FloorplanV2Plan,
    | 'origin_x_pixels'
    | 'origin_y_pixels'
    | 'image_pixels_per_meter'
    | 'measurement_point_a_x_pixels'
    | 'measurement_point_a_y_pixels'
    | 'measurement_point_b_x_pixels'
    | 'measurement_point_b_y_pixels'
    | 'measurement_computed_length_meters'
    | 'measurement_user_entered_length_feet'
    | 'measurement_user_entered_length_inches'
  > => {
    const measurementPointA = {
      x: measurement.pointA.x - origin.x,
      y: measurement.pointA.y - origin.y,
    };

    const measurementPointB = {
      x: measurement.pointB.x - origin.x,
      y: measurement.pointB.y - origin.y,
    };

    return {
      origin_x_pixels: resetOrigin ? 0 : origin.x,
      origin_y_pixels: resetOrigin ? 0 : origin.y,
      measurement_point_a_x_pixels: resetOrigin
        ? measurement.pointA.x
        : measurementPointA.x,
      measurement_point_a_y_pixels: resetOrigin
        ? measurement.pointA.y
        : measurementPointA.y,
      measurement_point_b_x_pixels: resetOrigin
        ? measurement.pointB.x
        : measurementPointB.x,
      measurement_point_b_y_pixels: resetOrigin
        ? measurement.pointB.y
        : measurementPointB.y,
      measurement_computed_length_meters: measurement.computedLength,
      measurement_user_entered_length_feet:
        measurement.userEnteredLength.feetText,
      measurement_user_entered_length_inches:
        measurement.userEnteredLength.inchesText,
      image_pixels_per_meter: measurement.computedScale,
    };
  },
  // Check if the floorplan has any elements (sensors, spaces, walls, etc.)
  // If there are no elements, we should reset the origin to 0,0
  hasFloorplanElements: (state: {
    walls: FloorplanCollection<any>;
    spaces: FloorplanCollection<any>;
    areasOfConcern: FloorplanCollection<any>;
    planSensors: FloorplanCollection<any>;
    references: FloorplanCollection<any>;
    thresholds: FloorplanCollection<any>;
    photoGroups: FloorplanCollection<any>;
  }): boolean => {
    return (
      !FloorplanCollection.isEmpty(state.walls) ||
      !FloorplanCollection.isEmpty(state.spaces) ||
      !FloorplanCollection.isEmpty(state.areasOfConcern) ||
      !FloorplanCollection.isEmpty(state.planSensors) ||
      !FloorplanCollection.isEmpty(state.references) ||
      !FloorplanCollection.isEmpty(state.thresholds) ||
      !FloorplanCollection.isEmpty(state.photoGroups)
    );
  },
};

export default Measurement;
